import React, { FC } from 'react';
import { dispatch } from 'nike-re-framejs';
import { initializeTurbolive } from 'nike-ui/src/components/turboLive/config';
import { getProperty } from '@/common/oldMobile';
import i18n from '@i18n';
import history from '@/routing/history';
import { CHANNEL_MOBILE } from 'nike-ui/src/components/turboLive/constants';
import TurboLiveContext from 'nike-ui/src/components/turboLive/context/TurboLiveContext';
import PopupNg from '@/ui/PopupNg';
import { clearMarketsSaveResponseStatus } from 'nike-ui/src/components/turboLive/favorite/handlers';
import { turboLiveContextValue } from '@/turbo-live/context/TurboLiveContextValue';
import StatsDialog from '@/common/components/statsDialog/StatsDialog';
import { DialogBody, DialogTitle, Docked } from '@/ui/dialogs/Dialog';
import { localizeMap } from 'nike-ui/src/components/turboLive/utils';
import BasicSelectionDialog from '@/ui/dialogs/BasicSelectionDialog';
import { registerPopup } from '@/ui/popupRegistrar';

const TurboLiveProvider: FC = ({ children }) => {
    initializeTurbolive(() => ({
        i18n,
        pushPathPrefix: getProperty('nikeonePushPathPrefix'),
        channel: CHANNEL_MOBILE,
        onError(error) {
            dispatch(['ui.showMasterError', 'error.unexpected.message', 'error.unexpected.title', null]);
        },
        onPageChanged(url) {
            window.scrollTo(0, 0);
            history.replace(url);
        },
        openDialog(title, body, options) {
            const close = () => {
                dispatch(clearMarketsSaveResponseStatus);
                if (options?.closeAll) {
                    PopupNg.dispatcher.closeAll();
                } else {
                    PopupNg.dispatcher.close('dialog/tlv/generic');
                }
            };
            PopupNg.dispatcher.open('dialog/tlv/generic', { title, body, close }, options?.priority);
            return close;
        },
        openStats(statisticsId, isTournament) {
            const close = () => {
                PopupNg.dispatcher.close('dialog/tlv/generic');
            };
            PopupNg.dispatcher.open('dialog/tlv/generic', {
                body: <StatsDialog statisticsId={statisticsId} isTournament={isTournament} />,
                dialogClass: 'theme-dark',
                bodyClass: 'p-0',
                close
            });
            return close;
        },
        openDialogMarketOptions(markets, onSelect, selectedMarket) {
            const close = () => PopupNg.dispatcher.close('dialog/tlv/marketOptions');
            PopupNg.dispatcher.open('dialog/tlv/marketOptions', { close, markets, onSelect, selectedMarket }, 10);
            return close;
        },
        configProperty(name) {
            return getProperty(name);
        }
    }));
    return <TurboLiveContext.Provider value={turboLiveContextValue}>{children}</TurboLiveContext.Provider>;
};

registerPopup('dialog/tlv/marketOptions', popup => <TlvMarketOptionsDialog {...popup.get('data')} />);

registerPopup('dialog/tlv/generic', popup => <GenericTlvDialog {...popup.get('data')} />);

function GenericTlvDialog({ title, body, close, dialogClass, bodyClass }) {
    return (
        <Docked className={dialogClass}>
            {title ? <DialogTitle>{title}</DialogTitle> : null}
            <DialogBody className={bodyClass}>{React.cloneElement(body, { close })}</DialogBody>
        </Docked>
    );
}

function TlvMarketOptionsDialog({ close, markets, onSelect, selectedMarket }) {
    const options = markets.map(market => {
        return {
            key: market.get('id'),
            value: localizeMap(market.get('name'))
        };
    });
    const selectedOption = {
        key: selectedMarket?.get('id'),
        value: selectedMarket?.get('name')
    };
    return (
        <BasicSelectionDialog
            options={options}
            onSelect={onSelect}
            selectedOption={selectedOption}
            close={close}
            title={i18n('tlv.marketOptionsDialog.title')}
        />
    );
}

export default TurboLiveProvider;
